import * as angular from 'angular';
import * as _ from 'lodash';
import template from './flexIsDetails.tpl.html';
import { States } from "../../shared/config/constant.module";
import { StateParams } from "../../common";
import UIUtilityServiceName, { UIUtilityService, EnumOption } from "../../services/utility/uiUtility.service";
import ShopServiceName, { ShopService } from "../../services/shops/shops.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import * as Highcharts from 'highcharts/highstock';
import { apsDisplayEnums } from '../../shared/dto/enums';
import { IInterval, Interval } from '../../directives/interval.factory';
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import * as moment from 'moment';
import { apsViewModels } from '../../shared/dto/APS.ViewModels';
import FlexIsDetailsServiceName, { FlexIsDetailsService } from '../../services/machineDetails/flexIsDetails.service';
import { apsEnums } from '../../shared/dto/aps.enums';

let requires = [
  UIUtilityServiceName,
  ShopServiceName,
  LoaderServiceName,
  AuthDataServiceName,
  FlexIsDetailsServiceName
];

export interface CavityStatistics {
  rpcRejectsBack: any[];
  rpcRejectsMiddle: any[];
  rpcRejectsFront: any[];
  blankPanelRejectsBack: any[];
  blankPanelRejectsMiddle: any[];
  blankPanelRejectsFront: any[];
  blowPanelRejectsBack: any[];
  blowPanelRejectsMiddle: any[];
  blowPanelRejectsFront: any[];
}

export interface SectionStatistics {
  sectionEfficiency: any[];
  mcGobsCut: any[];
  mcGobsNotCut: any[];
  mcGobsDelivered: any[];
  scGobsRequested: any[];
  scGobsNotRequested: any[];
  scGobsDelivered: any[];
  scLostMs: any[];
  scNrqStop: any[];
  scDelOff: any[];
  scNrqManSwab: any[];
  scNrqAltSwab: any[];
  scRjComo: any[];
  scRjManSwab: any[];
  scRjAltSwab: any[];
  scRjRpcLoss: any[];
  scRjBlankPanelLoss: any[];
  scRjBlowPanelLoss: any[];
  scRjWhc: any[];
  scRjTotal: any[];
}

export interface MachineControllerInfo {
  mcEfficiency: number;
  mcEfficiencyClass: string;
}

export class FlexIsDetailsController {
  plantId: string;
  shopId: string;
  shopName: string;
  plantName: string;
  chartOptions: any;
  startDate: Date;
  endDate: Date;
  hasFlexIS: boolean;
  bottlesPerMinute: number;
  numberOfSections: number;
  wareHandlingInfo: apsViewModels.WareHandlingInfo;
  sectionNumbers: number[];
  sectionStatistics: SectionStatistics;
  cavityStatistics: CavityStatistics;
  machineControllerInfo: MachineControllerInfo;
  countDisplayIntervalOptions: EnumOption[];
  countsInterval: apsDisplayEnums.CountDisplayInterval;
  $state: angular.ui.IStateService;
  loaderService: LoaderService;
  shopService: ShopService;
  uiUtilityService: UIUtilityService;
  shopConfig: apsViewModels.ShopConfigurationWithShifts;
  authDataService: AuthDataService;
  flexIsDetailsService: FlexIsDetailsService;
  $interval: angular.IIntervalService;
  interval: IInterval = null;
  $q: angular.IQService;
  private $scope: angular.IScope;

  constructor(
    $stateParams: StateParams,
    shopService: ShopService,
    loaderService: LoaderService,
    $scope: angular.IScope,
    uiUtilityService: UIUtilityService,
    authDataService: AuthDataService,
    flexIsDetailsService: FlexIsDetailsService,
    $state: angular.ui.IStateService,
    $interval: angular.IIntervalService,
    $q: angular.IQService) {

    this.plantId = $stateParams.plantId;
    this.shopId = $stateParams.shopId;
    this.loaderService = loaderService;
    this.shopService = shopService;
    this.shopName = null;
    this.plantName = null;
    this.flexIsDetailsService = flexIsDetailsService;
    this.$state = $state;
    this.$scope = $scope;
    this.uiUtilityService = uiUtilityService;
    this.authDataService = authDataService;
    this.$interval = $interval;
    this.$q = $q;

    this.$scope.$on('$destroy', () => {
      this.destroyInterval();
    });

    this.createInterval();
    this.initPage();
  }

  createInterval() {
    this.interval = new Interval(() => {
      this.update();
    }, this.$interval, 1000 * 60 * 5);
    this.interval.start();
  }

  destroyInterval() {
    this.interval.destroy();
    this.interval = null;
  }

  countsIntervalChanged(): void {
    this.mapCountInterval();
    this.update();
  }

  initPage() {
    this.loaderService.setAppLoading(true);
    this.shopService.getShopConfiguration(this.shopId)
      .then(shop => {
        this.shopName = shop.name;
        this.plantName = shop.plantName;
        this.shopConfig = shop;
      })
      .then(() => {
        this.countDisplayIntervalOptions = this.uiUtilityService
          .getListOptionsFromEnum(apsDisplayEnums.CountDisplayInterval, null, null, o => o);
      })
      .then(() => {
        this.countsInterval = this.authDataService.getCountInterval();
        this.mapCountInterval();
      })
      .then(() => {
        this.flexIsDetailsService
          .getFlexIsDetails(this.shopId, this.startDate, this.endDate)
          .then((result) => {
            this.mapResult(result);
            this.loaderService.setAppLoading(false);
          });
      });
  }

  update() {
    this.loaderService.setAppLoading(true);
    this.flexIsDetailsService
      .getFlexIsDetails(this.shopId, this.startDate, this.endDate)
      .then((result) => {
        this.mapResult(result);
        this.loaderService.setAppLoading(false);
      });
  }

  mapResult(result: apsViewModels.FlexIsDetailsInfo) {
    this.bottlesPerMinute = result.bottlesPerMinute || 0.0;
    this.mapSwabbingData(result.swabbingTrendSummaries);
    this.mapWareHandlingInfo(result.warehandling);
    this.mapSectionStatistics(result.sectionCountSummaries);
    this.mapSectionCavitySummary(result.sectionCavityCountSummaries);
    this.mapMachineControllerInfo(result.machineControllerInfo);
    this.hasFlexIS = result.hasFlexIS;
  }

  mapMachineControllerInfo(machineControllerInfo: apsViewModels.MachineControllerInfo) {
    let mcEfficiency = machineControllerInfo.mcEfficiency || 0.0;
    let mcEfficiencyClass = this.getEfficiencyColor(mcEfficiency);

    this.machineControllerInfo = {
      mcEfficiency: mcEfficiency,
      mcEfficiencyClass: mcEfficiencyClass
    };
  }

  mapSectionCavitySummary(sectionCavityCountSummaries: apsViewModels.SectionCavitySummary[]) {
    let rpcRejectsBack = [];
    let rpcRejectsMiddle = [];
    let rpcRejectsFront = [];
    let blankPanelRejectsBack = [];
    let blankPanelRejectsMiddle = [];
    let blankPanelRejectsFront = [];
    let blowPanelRejectsBack = [];
    let blowPanelRejectsMiddle = [];
    let blowPanelRejectsFront = [];

    sectionCavityCountSummaries.forEach(x => {
      x.details.forEach(y => {
        if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_SRV_X && y.cavity === "B") {
          rpcRejectsBack.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_SRV_X && y.cavity === "M") {
          rpcRejectsMiddle.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_SRV_X && y.cavity === "F") {
          rpcRejectsFront.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_BK_X && y.cavity === "B") {
          blankPanelRejectsBack.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_BK_X && y.cavity === "M") {
          blankPanelRejectsMiddle.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_BK_X && y.cavity === "F") {
          blankPanelRejectsFront.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_BW_X && y.cavity === "B") {
          blowPanelRejectsBack.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_BW_X && y.cavity === "M") {
          blowPanelRejectsMiddle.push({
            section: x.displaySection,
            count: y.count
          });
        } else if (y.aliasId === apsEnums.FlexISDataPointAliases.SC_RJ_BW_X && y.cavity === "F") {
          blowPanelRejectsFront.push({
            section: x.displaySection,
            count: y.count
          });
        }
      });
    });

    this.cavityStatistics = {
      rpcRejectsBack: rpcRejectsBack,
      rpcRejectsMiddle: rpcRejectsMiddle,
      rpcRejectsFront: rpcRejectsFront,
      blankPanelRejectsBack: blankPanelRejectsBack,
      blankPanelRejectsMiddle: blankPanelRejectsMiddle,
      blankPanelRejectsFront: blankPanelRejectsFront,
      blowPanelRejectsBack: blowPanelRejectsBack,
      blowPanelRejectsMiddle: blowPanelRejectsMiddle,
      blowPanelRejectsFront: blowPanelRejectsFront
    };
  }

  mapSectionStatistics(sectionCountSummaries: apsViewModels.SectionCountSummary[]) {
    this.sectionNumbers = this.mapSectionNumbers(sectionCountSummaries);
    let sectionEfficiency = this.mapSectionEfficiency(sectionCountSummaries);
    let sectionDetails = _.chain(sectionCountSummaries)
      .map('details')
      .flatten()
      .value();

    // mc values
    let mcGobsCut = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.MC_CUT_SC_X);
    let mcGobsNotCut = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.MC_NOT_CUT_SC_X);
    let mcGobsDelivered = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.MC_DEL_SC_X);

    // section request and delivery
    let scGobsRequested = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_REQ);
    let scGobsNotRequested = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_NRQ);
    let scGobsDelivered = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_DEL);

    // section not request
    let scLostMs = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_LOST_MS);
    let scNrqStop = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_NRQ_STOP);
    let scDelOff = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_NRQ_DEL_OFF);
    let scNrqManSwab = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_NRQ_MAN_SWAB);
    let scNrqAltSwab = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_NRQ_ALT_SWAB);

    // section rejected
    let scRjComo = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_COMO);
    let scRjManSwab = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_MAN_SWAB);
    let scRjAltSwab = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_ALT_SWAB);
    let scRjRpcLoss = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_SRV);
    let scRjBlankPanelLoss = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_BK);
    let scRjBlowPanelLoss = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_BW);
    let scRjWhc = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ_WHC);
    let scRjTotal = this.mapSectionValues(sectionDetails, apsEnums.FlexISDataPointAliases.SC_RJ);

    this.sectionStatistics = {
      mcGobsCut: mcGobsCut,
      mcGobsNotCut: mcGobsNotCut,
      mcGobsDelivered: mcGobsDelivered,
      scGobsRequested: scGobsRequested,
      scGobsNotRequested: scGobsNotRequested,
      scGobsDelivered: scGobsDelivered,
      scLostMs: scLostMs,
      scNrqStop: scNrqStop,
      scDelOff: scDelOff,
      scNrqManSwab: scNrqManSwab,
      scNrqAltSwab: scNrqAltSwab,
      scRjComo: scRjComo,
      scRjManSwab: scRjManSwab,
      scRjAltSwab: scRjAltSwab,
      scRjRpcLoss: scRjRpcLoss,
      scRjBlankPanelLoss: scRjBlankPanelLoss,
      scRjBlowPanelLoss: scRjBlowPanelLoss,
      scRjWhc: scRjWhc,
      scRjTotal: scRjTotal,
      sectionEfficiency: sectionEfficiency
    };
  }

  mapSectionEfficiency(sectionCountSummaries: apsViewModels.SectionCountSummary[]) {
    let sectionEfficiency = [];
    let numberOfSections = sectionCountSummaries.length;

    for (let i = 0; i < numberOfSections; i++) {
      let efficiency = sectionCountSummaries[i].sectionEfficiency || 0.0;
      let efficiencyColor = this.getEfficiencyColor(efficiency);
      sectionEfficiency.push({
        efficiency: efficiency,
        efficiencyColor: efficiencyColor
      });
    }

    return sectionEfficiency;
  }

  getEfficiencyColor(efficiency: number) {
    let efficiencyColor = 'danger';

    if (efficiency >= 99) {
      efficiencyColor = 'success';
    } else if (efficiency >= 98) {
      efficiencyColor = 'warning';
    }

    return efficiencyColor;
  }

  mapSectionValues(sectionDetails, aliasEnum): any[] {
    let sectionValues = _.filter(sectionDetails, { aliasId: aliasEnum });
    return sectionValues;
  }

  mapSectionNumbers(sectionCountSummaries: apsViewModels.SectionCountSummary[]): number[] {
    let numberArray = _.flatMap(sectionCountSummaries, (s) => {
      return s.section;
    });
    this.numberOfSections = numberArray.length;
    return numberArray;
  }

  mapWareHandlingInfo(warehandling: apsViewModels.WareHandlingInfo) {
    this.wareHandlingInfo = warehandling;
  }

  mapSwabbingData(swabbingTrendSummary: apsViewModels.SwabbingTrendSummary[]) {
    this.chartOptions = this.getChartOptions(swabbingTrendSummary);
    Highcharts.chart('container', this.chartOptions);
  }

  mapCountInterval() {
    this.authDataService.setCountInterval(this.countsInterval);
    this.endDate = moment().toDate();
    let now = moment();
    let remainderMinutes = now.minute() % 10;
    let remainderSeconds = now.second();
    let remainderMilliSeconds = now.millisecond();
    switch (this.countsInterval) {
      case apsDisplayEnums.CountDisplayInterval.CurrentHour:
        this.startDate = moment().startOf('hour').toDate();
        break;
      case apsDisplayEnums.CountDisplayInterval.PreviousHour:
        this.endDate = moment().startOf('hour').toDate();
        this.startDate = moment(this.endDate).subtract(1, 'hour').toDate();
        break;
      case apsDisplayEnums.CountDisplayInterval.CurrentShift:
        this.startDate = this.shopConfig.shiftsInLast24Hours[0].startTime;
        break;
      case apsDisplayEnums.CountDisplayInterval.LastShift:
        this.endDate = this.shopConfig.shiftsInLast24Hours[1].stopTime;
        this.startDate = this.shopConfig.shiftsInLast24Hours[1].startTime;
        break;
      case apsDisplayEnums.CountDisplayInterval.ShiftBeforeLast:
        this.endDate = this.shopConfig.shiftsInLast24Hours[2].stopTime;
        this.startDate = this.shopConfig.shiftsInLast24Hours[2].startTime;
        break;
      case apsDisplayEnums.CountDisplayInterval.RollingHour:
				this.endDate = moment(now)
					.subtract(remainderMinutes, 'minute')
					.subtract(remainderSeconds, 'second')
					.subtract(remainderMilliSeconds, 'millisecond')
					.toDate();
				this.startDate = moment(this.endDate)
					.subtract(1, 'hour')
					.toDate();
        break;
      case apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes:
				this.endDate = moment(now)
					.subtract(remainderMinutes, 'minute')
					.subtract(remainderSeconds, 'second')
					.subtract(remainderMilliSeconds, 'millisecond')
					.toDate();
				this.startDate = moment(this.endDate)
					.subtract(20, 'minute')
					.toDate();
        break;
      default: // default to 24 hours
        this.endDate = moment().startOf('hour').toDate();
        this.startDate = moment(this.endDate).subtract(1, 'days').toDate();
        break;
    }
  }

  getCategories() {
    const categoryList = [];

    for (let i = 1; i <= 16; i++) {
      categoryList.push('Section ' + i);
    }

    return categoryList;
  }

  getSampleData(details: apsViewModels.SwabbingTrendDetails[]) {
    const sampleSeries = [];

    details.forEach(x => {
      let dateTime = new Date(x.isoTimeStamp);
      let datapoint = {
        x: moment(dateTime).toDate().getTime(),
        y: x.count
      };
      sampleSeries.push(datapoint);
    });

    return sampleSeries;
  }

  getSeries(swabbingTrendSummary: apsViewModels.SwabbingTrendSummary[]) {
    const seriesData = [];

    swabbingTrendSummary.forEach(x => {
      const item = {
        name: 'Section ' + x.section,
        data: this.getSampleData(x.details),
        legendIndex: x.section
      };
      seriesData.push(item);
    });

    return seriesData;
  }

  getChartOptions(swabbingTrendSummary: apsViewModels.SwabbingTrendSummary[]) {
    let seriesData = this.getSeries(swabbingTrendSummary);
    return {

      chart: {
        zoomType: 'x'
      },

      time: {
        useUTC: false
      },

      title: {
        text: ''
      },

      yAxis: {
        title: {
          text: 'Number of Gobs Lost'
        }
      },

      xAxis: {
        type: 'datetime',
        title: {
          text: 'Date/Time'
        },
        dateTimeLabelFormats: {
          second: '%l:%M:%S %p',
          minute: '%l:%M %p',
          hour: '%l %p',
          day: '%m/%d/%Y',
          month: '%m/%d/%Y',
          year: '%m/%d/%Y'
        }
      },

      tooltip: {
        xDateFormat: '%m/%d/%Y %l:%M %p'
      },

      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },

      plotOptions: {
        series: {
          marker: {
            enabled: false
          }
        }
      },

      series: seriesData,

      credits: {
        enabled: false
      },

      responsive: {
        rules: [{
          condition: {
            maxWidth: 500
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom'
            }
          }
        }]
      }
    };
  }
}

export default angular.module('aps.controller.flexIsDetails', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.FLEX_IS_DETAILS,
      url: '/flexIsDetails',
      controller: 'FlexIsDetailsCtrl as vm',
      template: template,
      data: {
        pageTitle: 'FlexIS Details'
      }
    });
  })
  .controller('FlexIsDetailsCtrl', FlexIsDetailsController)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/machineDetails/flexIsDetails.controller.ts